// Toaster — renders the live toast stack in a fixed bottom-right column.
// Reads the dependency-free store in src/lib/toast.ts via
// useSyncExternalStore, so it stays in sync with imperative toast.*
// calls made anywhere (notably the MutationCache in main.tsx, which fires
// a toast for every opted-in action mutation's success/failure).
//
// Sits above the modal layer (modal-back is z-index:100) so confirmations
// stay visible over open dialogs.
import { useSyncExternalStore } from "react";
import { subscribe, getSnapshot, dismissToast, type Toast, type ToastKind } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { useTranslation } from "react-i18next";

const TONE: Record<ToastKind, { card: string; icon: string }> = {
  success: { card: "border-yes/40", icon: "bg-yes/15 text-yes" },
  error: { card: "border-no/45", icon: "bg-no/15 text-no" },
  info: { card: "border-line-2", icon: "bg-neon/15 text-neon-2" },
};

function ToastIcon({ kind }: { kind: ToastKind }) {
  if (kind === "success") {
    return (
      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3.5 8.5l3 3 6-6.5" />
      </svg>
    );
  }
  if (kind === "error") {
    return (
      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M4 4l8 8M12 4l-8 8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
      <path d="M8 1.5A6.5 6.5 0 108 14.5 6.5 6.5 0 008 1.5zM8 4a.9.9 0 110 1.8A.9.9 0 018 4zm1 8H7v-1h.6V7.6H7v-1h2V11h.6v1z" />
    </svg>
  );
}

function ToastCard({ toast }: { toast: Toast }) {
  const { t } = useTranslation();
  const tone = TONE[toast.kind];
  return (
    <div
      role={toast.kind === "error" ? "alert" : "status"}
      className={cn(
        "toast-enter pointer-events-auto flex items-start gap-3 rounded-lg border bg-bg-2/95 px-3.5 py-3 shadow-xl backdrop-blur-sm",
        tone.card,
      )}
    >
      <span className={cn("mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full", tone.icon)}>
        <ToastIcon kind={toast.kind} />
      </span>
      <p className="min-w-0 flex-1 break-words text-sm leading-snug text-t-1">{toast.message}</p>
      <button
        type="button"
        onClick={() => dismissToast(toast.id)}
        aria-label={t("common.dismiss")}
        className="-mr-1 -mt-0.5 shrink-0 rounded p-1 text-t-3 transition-colors hover:text-t-1"
      >
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  );
}

export function Toaster() {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (toasts.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[200] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}
